// Copilot de AGENTES de voz. Diferente do copilot de FLUXO (copilot.js), este NÃO
// injeta o catálogo de nós — configurar um agente é um conjunto fixo e pequeno de
// campos. A criação de FERRAMENTA (uma tool HTTP) é "pesada" e só entra no contexto
// quando o modelo pede (request_tool_builder) — os 95% que só configuram persona/voz
// nunca pagam esse custo. Ver handoff de tokens.

// vozes por provider (espelha AgentEditor.PROVIDERS). eleven é dinâmico (catálogo).
export const VOICES = {
  gemini: ["Puck", "Charon", "Kore", "Fenrir", "Aoede", "Leda", "Orus", "Zephyr"],
  openai: ["alloy", "ash", "ballad", "coral", "echo", "sage", "shimmer", "verse"],
  gptlive: ["bossa", "tempo", "marin"],
  grok: ["eve", "ara", "rex", "sal", "leo"],
  nova: ["matthew", "tiffany", "amy"],
  eleven: [], // vozes vêm do catálogo do ElevenLabs (não sugerir por aqui)
};

// ── TOOLS BASE (sempre presentes; schema pequeno) ──────────────────────────
export const BASE_TOOLS = [
  {
    name: "set_name",
    description: "Define o nome do agente — curto e descritivo do papel (ex.: 'Atendente Motel Prime', 'Cobrança amigável').",
    input_schema: { type: "object", properties: { name: { type: "string" } }, required: ["name"] },
  },
  {
    name: "read_agent",
    description: "Lê a configuração ATUAL do agente (persona, saudação, provider, voz, transferências, campos de resultado) e a lista de ferramentas já existentes na conta. Chame no início para saber o que já está posto antes de mudar algo.",
    input_schema: { type: "object", properties: {} },
  },
  {
    name: "fetch_url",
    description:
      "Lê um site/página PÚBLICA e devolve o texto principal (home + algumas páginas internas relevantes, como produtos/preços/sobre). Use quando o usuário der uma URL ou nome de empresa com site (ex.: 'cria um atendente para a BR DID: brdid.com.br') para embasar a persona/objetivo/produtos no conteúdo real. Chame ANTES de escrever a persona. O conteúdo pode vir cortado por limites de volume (truncated) e sites feitos em JS podem trazer só um resumo — nesses casos, complemente com o que o usuário disser.",
    input_schema: { type: "object", properties: { url: { type: "string", description: "URL ou domínio do site (ex.: brdid.com.br ou https://brdid.com.br)." } }, required: ["url"] },
  },
  {
    name: "set_persona",
    description:
      "Define a PERSONA (instrução/prompt de sistema) do agente — quem ele é, o objetivo da ligação, tom e regras. Escreva completo e específico. Substitui a persona atual. FORMATE em markdown (o editor renderiza): use seções com títulos, listas e destaques.",
    input_schema: {
      type: "object",
      properties: {
        persona: {
          type: "string",
          description:
            "A persona completa, em pt-BR, FORMATADA em markdown para ficar legível no editor. Formatações suportadas (use-as sempre): '## Título' para as seções principais (ex.: ## Papel e Persona, ## Objetivo, ## Fluxo da conversa, ## Limites e Escalada, ## Voz e Estilo); '### Subtítulo' para subseções; '- item' para listas; '1. item' para passos numerados; '**negrito**' para termos-chave; '> texto' para um DESTAQUE do que EVITAR/regras críticas (vira um box de aviso). Organize sempre em seções com ## — nada de um bloco de texto corrido.",
        },
      },
      required: ["persona"],
    },
  },
  {
    name: "set_greeting",
    description: "Define a saudação inicial que o agente fala ao atender/ligar. Curta e natural. Pode usar marcadores como <nome> que são preenchidos pela persona.",
    input_schema: { type: "object", properties: { greeting: { type: "string" } }, required: ["greeting"] },
  },
  {
    name: "set_voice",
    description: "Define o provider de voz e a voz. Providers e vozes válidos vêm em read_agent. Padrão recomendado: gemini + Aoede (voz natural em pt-BR). Só use eleven se o usuário pedir voz clonada/específica.",
    input_schema: {
      type: "object",
      properties: {
        provider: { type: "string", description: "gemini | openai | gptlive | grok | nova | eleven" },
        voice: { type: "string", description: "nome da voz (uma das listadas para o provider)." },
      },
      required: ["provider", "voice"],
    },
  },
  {
    name: "set_transfers",
    description: "Define os destinos de transferência para humano (o agente escolhe pelo rótulo durante a conversa). Substitui a lista atual. Passe [] para remover todas.",
    input_schema: {
      type: "object",
      properties: {
        transfers: { type: "array", items: { type: "object", properties: { label: { type: "string" }, number: { type: "string" } }, required: ["label", "number"] } },
      },
      required: ["transfers"],
    },
  },
  {
    name: "set_outputs",
    description: "Define o SCHEMA DE RESULTADO da ligação — os campos que o agente registra ao encerrar (ex.: confirmou:boolean, motivo:string). Substitui os campos atuais. Passe [] para nenhum.",
    input_schema: {
      type: "object",
      properties: {
        outputs: { type: "array", items: { type: "object", properties: {
          name: { type: "string" }, type: { type: "string", description: "string | number | boolean | enum" },
          description: { type: "string" }, options: { type: "array", items: { type: "string" }, description: "valores, se type=enum" },
        }, required: ["name", "type"] } },
      },
      required: ["outputs"],
    },
  },
  {
    name: "attach_tool",
    description: "Anexa uma ferramenta JÁ EXISTENTE ao agente pelo id (ver a lista em read_agent). Use para reaproveitar ferramentas prontas.",
    input_schema: { type: "object", properties: { tool_id: { type: "number" } }, required: ["tool_id"] },
  },
  {
    name: "detach_tool",
    description: "Remove uma ferramenta do agente pelo id.",
    input_schema: { type: "object", properties: { tool_id: { type: "number" } }, required: ["tool_id"] },
  },
  {
    name: "request_tool_builder",
    description: "Chame ISTO quando o agente precisar de uma ação externa que ainda NÃO existe como ferramenta — ex.: consultar um pedido/disponibilidade/CEP numa API, agendar, checar estoque. Habilita a ferramenta create_tool para você criar a função. NÃO chame se já existe uma ferramenta que serve (use attach_tool) nem para agentes puramente conversacionais.",
    input_schema: { type: "object", properties: { reason: { type: "string", description: "o que a ferramenta precisa fazer, em 1 frase." } }, required: ["reason"] },
  },
  {
    name: "ask_user",
    description: "Faz UMA pergunta objetiva ao usuário quando falta um dado que você não tem como inferir (ex.: a URL da API, o número de transferência). Prefira montar com padrões e só perguntar o essencial.",
    input_schema: {
      type: "object",
      properties: {
        question: { type: "string" },
        kind: { type: "string", description: "text | choice | multi" },
        options: { type: "array", items: { type: "string" } },
      },
      required: ["question"],
    },
  },
];

// ── TOOL PESADA (só entra após request_tool_builder) ───────────────────────
export const CREATE_TOOL = {
  name: "create_tool",
  description:
    "Cria uma ferramenta HTTP para o agente e a anexa. A ferramenta chama uma API externa e devolve a resposta ao agente. Os inputParams viram os parâmetros que o agente preenche na conversa; referencie-os na url/body como {{steps.trigger.input.NOME}}. A resposta da API fica em {{steps.http_request.json}} (JSON) ou {{steps.http_request.body}} (texto).",
  input_schema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Nome curto da ferramenta (ex.: 'Consultar disponibilidade')." },
      description: { type: "string", description: "O que a ferramenta faz — a IA lê isto para decidir quando chamá-la. Seja claro." },
      inputParams: {
        type: "array",
        description: "Parâmetros que o agente preenche ao chamar. Vazio se não houver.",
        items: { type: "object", properties: {
          name: { type: "string", description: "identificador (a-z, 0-9, _)." },
          type: { type: "string", description: "string | number | boolean" },
          description: { type: "string" },
          required: { type: "boolean" },
        }, required: ["name", "type"] },
      },
      method: { type: "string", description: "GET | POST | PUT | PATCH | DELETE" },
      url: { type: "string", description: "URL da API. Use {{steps.trigger.input.NOME}} para inserir os parâmetros (ex.: em querystring)." },
      headers: { type: "object", description: "Headers como objeto {chave: valor}. Inclua Authorization aqui se a API exigir token." },
      body: { type: "string", description: "Corpo (JSON como texto) para POST/PUT/PATCH. Use {{steps.trigger.input.NOME}}." },
      resultPath: { type: "string", description: "Opcional: caminho dentro do JSON de resposta a retornar (ex.: 'data.rooms'). Vazio = retorna o JSON inteiro." },
    },
    required: ["name", "description", "method", "url"],
  },
};

// contexto real: config atual do agente + ferramentas existentes + conectores.
export const buildAgentContext = (agent, tools, connectors) => {
  const lines = [];
  lines.push("## Agente atual");
  lines.push(`Nome: ${agent.name || "(sem nome)"}`);
  lines.push(`Provider: ${agent.provider || "gemini"} · Voz: ${agent.voice || "(não definida)"}`);
  lines.push(`Persona: ${agent.persona ? "definida" : "vazia"} · Saudação: ${agent.greeting ? "definida" : "vazia"}`);
  lines.push(`Transferências: ${(agent.transferDestinations || []).length} · Campos de resultado: ${(agent.outputs || []).length}`);
  lines.push(`Ferramentas anexadas (toolIds): ${JSON.stringify(agent.toolIds || [])}`);
  lines.push("");
  lines.push("## Ferramentas existentes na conta (para attach_tool)");
  if (!tools || !tools.length) lines.push("(nenhuma)");
  else tools.forEach(t => lines.push(`- id ${t.id}: ${t.name}${t.description ? ` — ${t.description}` : ""}`));
  lines.push("");
  lines.push("## Vozes por provider");
  Object.keys(VOICES).forEach(p => lines.push(`- ${p}: ${VOICES[p].length ? VOICES[p].join(", ") : "(catálogo dinâmico)"}`));
  if (connectors && connectors.length) {
    lines.push("");
    lines.push("## Conectores configurados (auth possível para ferramentas HTTP)");
    connectors.forEach(c => lines.push(`- ${c.type}${c.name ? ` (${c.name})` : ""}`));
  }
  return lines.join("\n");
};

// system prompt. `toolBuilder` só é true depois de request_tool_builder — aí some o
// custo do guia de criação de ferramenta para os 95% que não criam nenhuma.
export const buildAgentSystem = (context, { toolBuilder } = {}) => {
  const base = `Você é o copiloto do Numera Studio para criar e configurar AGENTES DE VOZ (atendentes de IA que falam ao telefone), em português do Brasil.

Um agente tem: PERSONA (quem é + objetivo + regras), SAUDAÇÃO inicial, VOZ (provider + voz), TRANSFERÊNCIAS para humano, CAMPOS DE RESULTADO (o que registrar ao fim) e FERRAMENTAS (funções que ele chama na conversa para agir/consultar sistemas).

REGRAS:
- MONTE PRIMEIRO, PERGUNTE DEPOIS: a partir do pedido do usuário, configure uma versão completa e boa (persona rica, saudação natural, voz padrão gemini+Aoede) usando as tools. Só use ask_user para o que você realmente não tem como inferir (ex.: URL de uma API, número de transferência).
- Uma tool por turno. Comece com read_agent para ver o que já existe.
- SITE/URL: se o usuário citar um site ou domínio da empresa (ex.: "cria um atendente para a BR DID: brdid.com.br"), chame fetch_url ANTES de montar a persona e use o conteúdo real (o que a empresa faz, produtos, tom) para embasar persona, saudação e objetivo. Se o conteúdo vier curto/truncado, use o que der e complemente com bom senso.
- Persona: escreva de verdade — papel, objetivo da ligação, tom, o que fazer e o que NÃO fazer, como encerrar. Nada de placeholder preguiçoso.
- Voz: padrão gemini + Aoede (natural em pt-BR). Só troque se o usuário pedir.
- FERRAMENTAS: a maioria dos agentes é conversacional e NÃO precisa de ferramenta. Só crie ferramenta quando o agente precisar AGIR num sistema externo (consultar/gravar via API). Se já existe uma ferramenta que serve, use attach_tool. Se precisa de uma nova, chame request_tool_builder (isso habilita create_tool).
- Fale com o usuário em texto curto entre as ações, explicando o que montou.`;

  const toolGuide = toolBuilder
    ? `

## Criando ferramentas (create_tool está disponível)
Uma ferramenta é uma função HTTP: o agente preenche os inputParams na conversa e ela chama uma API.
- inputParams = os dados que o agente coleta (ex.: {name:"cep", type:"string"}). Referencie-os como {{steps.trigger.input.cep}} na url/body.
- A resposta da API fica em {{steps.http_request.json}} (JSON) — use resultPath para extrair um campo (ex.: "data.rooms").
- Auth: se a API exige token, ponha em headers (ex.: {"Authorization":"Bearer ..."}). Peça o token via ask_user se não souber.
- Dê um description CLARO na ferramenta — é o que o agente lê para decidir quando chamá-la.
Depois de criar, a ferramenta já é anexada ao agente automaticamente.`
    : "";

  return `${base}${toolGuide}\n\n---\n${context}`;
};
